import { Grid, GridItem, Portal } from "@chakra-ui/react";
import React, { createContext, RefObject, useContext, useRef } from "react";
import { Outlet } from "react-router-dom";

interface Props {
  header: React.ReactNode;
  leftNav: React.ReactNode;
  alert?: React.ReactNode;
}

type AlertContextType = { alertContainerRef: RefObject<HTMLDivElement> | null };
const AlertContext = createContext<AlertContextType>({
  alertContainerRef: null,
});

export const BaseLayout = (props: Props) => {
  const alertContainerRef = useRef<HTMLDivElement>(null);
  return (
    <AlertContext.Provider value={{ alertContainerRef }}>
      <Grid
        width="100vw"
        height="100vh"
        templateRows="5.125rem auto 1fr"
        templateColumns="auto 1fr"
        templateAreas='"header header""leftNav alert""leftNav main"'
        sx={{
          "@media print": {
            gridTemplateAreas: '"main"',
          },
        }}
      >
        <GridItem area="header" zIndex="11">
          {props.header}
        </GridItem>
        <GridItem
          display="flex"
          area="leftNav"
          position="relative"
          overflow="hidden"
          data-print-hide
        >
          {props.leftNav}
        </GridItem>
        <GridItem area="alert" ref={alertContainerRef}>
          {props.alert}
        </GridItem>
        <GridItem
          as="main"
          area="main"
          overflow="auto"
          padding="10"
          sx={{
            "@media print": {
              overflow: "visible",
            },
          }}
        >
          <Outlet />
        </GridItem>
      </Grid>
    </AlertContext.Provider>
  );
};

export function useAlertPortal() {
  const context = useContext(AlertContext);
  const alertContainerRef = context.alertContainerRef;
  if (alertContainerRef === null) {
    throw new Error("useAlert must be called inside the BaseLayout");
  }

  return {
    AlertPortal: (props: { children: React.ReactNode }) => (
      <Portal containerRef={alertContainerRef}>{props.children}</Portal>
    ),
  };
}
